/**
 * File path and filename sanitization utilities
 * 
 * Provides defensive sanitization to prevent:
 * - Directory traversal attacks (../, ..\\)
 * - Path injection
 * - Invalid characters in filenames
 * - Unsafe file paths
 */

/**
 * Sanitize a filename by removing dangerous characters and path components
 * @param {string} filename - Original filename
 * @param {string} fallback - Fallback name if sanitization results in empty string
 * @returns {string} Sanitized filename safe for use
 */
export const sanitizeFilename = (filename, fallback = 'file') => {
  if (!filename || typeof filename !== 'string') {
    return fallback;
  }
  
  // Remove path components (directory traversal attempts)
  let sanitized = filename
    .replace(/\.\./g, '') // Remove .. sequences
    .replace(/[/\\]/g, '_') // Replace path separators with underscore
    // eslint-disable-next-line no-control-regex -- strip C0 controls (NUL..US) from filenames
    .replace(/[<>:"|?*\x00-\x1f]/g, "") // Remove invalid filename characters
    .trim();
  
  // Remove leading/trailing dots and spaces (Windows doesn't allow these)
  sanitized = sanitized.replace(/^[\s.]+|[\s.]+$/g, '');
  
  // Ensure filename is not empty
  if (!sanitized || sanitized.length === 0) {
    return fallback;
  }
  
  // Limit length to prevent issues (255 chars is typical max)
  if (sanitized.length > 255) {
    const ext = sanitized.substring(sanitized.lastIndexOf('.'));
    const name = sanitized.substring(0, 255 - ext.length);
    sanitized = name + ext;
  }
  
  return sanitized;
};

/**
 * Sanitize a file path by removing dangerous components
 * @param {string} filePath - Original file path
 * @returns {string} Sanitized path safe for use
 */
export const sanitizeFilePath = (filePath) => {
  if (!filePath || typeof filePath !== 'string') {
    return '';
  }
  
  // Remove directory traversal attempts
  let sanitized = filePath
    .replace(/\.\./g, '') // Remove .. sequences
    // eslint-disable-next-line no-control-regex -- strip C0 controls from paths
    .replace(/[<>:"|?*\x00-\x1f]/g, "") // Remove invalid path characters
    .trim();
  
  // Normalize path separators (use forward slash)
  sanitized = sanitized.replace(/\\/g, '/');
  
  // Remove leading slashes to prevent absolute paths
  sanitized = sanitized.replace(/^\/+/, '');
  
  return sanitized;
};

/**
 * Validate that a file path doesn't contain dangerous patterns
 * @param {string} filePath - File path to validate
 * @returns {boolean} True if path appears safe, false otherwise
 */
export const isSafeFilePath = (filePath) => {
  if (!filePath || typeof filePath !== 'string') {
    return false;
  }
  
  // Check for directory traversal patterns
  if (filePath.includes('..') || filePath.includes('../') || filePath.includes('..\\')) {
    return false;
  }
  
  // Check for absolute paths (starting with / or drive letter)
  if (/^[/\\]|^[A-Za-z]:[/\\]/.test(filePath)) {
    return false;
  }
  
  // Check for invalid characters
  // eslint-disable-next-line no-control-regex -- reject C0 controls in paths
  if (/[<>:"|?*\x00-\x1f]/.test(filePath)) {
    return false;
  }
  
  return true;
};

/**
 * Extract filename from a path and sanitize it
 * @param {string} filePath - Full file path
 * @param {string} fallback - Fallback name if extraction fails
 * @returns {string} Sanitized filename
 */
export const extractAndSanitizeFilename = (filePath, fallback = 'file') => {
  if (!filePath || typeof filePath !== 'string') {
    return fallback;
  }
  
  // Extract filename (last component after path separator)
  const filename = filePath.split(/[/\\]/).pop() || filePath;
  
  return sanitizeFilename(filename, fallback);
};

